import React from 'react';
import {
    AddCircle16Filled,
    SubtractCircle16Regular,
    Star16Regular,
    Star16Filled,
    CommentAdd16Regular,
    Comment16Filled,
    ErrorCircle16Regular,
    Checkmark16Regular,
    ArrowMaximize16Regular,
    MoreVertical16Regular,
    Attach16Regular,
    Link16Regular,
    Add16Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Tooltip } from '@fluentui/react-components';
import { CustomAvatar } from './index';
import TagManagementMenu from './TagManagementMenu';
import WorkstreamManagementMenu from './WorkstreamManagementMenu';
import ProjectPhaseManagementMenu from './ProjectPhaseManagementMenu';
import DeadlinePickerMenu from './TimelinePickerMenu';
import TimelineRangePickerMenu from './TimelineRangePickerMenu';
import ModePickerMenu from './ModePickerMenu';
import DurationPickerMenu from './DurationPickerMenu';
import UserAssignmentMenu from './UserAssignmentMenu';
import styles from './css/index.module.css';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useDispatch, useSelector } from 'react-redux';
import { UploadTaskAttachment, PatchTask } from '../../redux/taskSlice';
import { isValidExtensionToUpload } from '../../utils/commonUtils';
import { validExtensions } from '../../utils/misc';
import { getChildren, getDropIntentMessage, getInheritedDeadline, getInheritedDuration, getInheritedTimeline, sortTasksByOrder } from '../../utils/taskHierarchyUtils';
import { DEFAULT_TASK_TABLE_COLUMN_ORDER } from '../../utils/taskTableColumns';
import {
    formatDuration,
    getStatusOptionsForProject,
    getVisibleTaskTags,
    isSchedulingMode,
    normalizeTaskStatus,
    taskIsOffTrack,
} from '../../utils/taskSchedulingUtils';

const combineRefs =
    (...refs) =>
    node => {
        refs.forEach(ref => {
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
        });
    };

const isTaskSelected = (selectedRows, taskId) => {
    if (!selectedRows?.length) return false;
    const id = String(taskId);
    return selectedRows.some(rowId => String(rowId) === id);
};

const HierarchicalTaskRow = ({
    task,
    index,
    editingTaskId,
    setEditingTaskId,
    editingTaskText,
    setEditingTaskText,
    handleTaskTextClick,
    handleTaskTextSave,
    handleTaskTextCancel,
    handleTaskTextKeyPress,
    handleStarClick,
    handleUserAssignment,
    handleDeadlineSelect,
    handleStatusChange,
    handlePriorityChange,
    handleTagSelect,
    handleWorkstreamSelect,
    handleEdit,
    handleDelete,
    handleSelectRow,
    selectedRows,
    isColumnVisible,
    columnWidths,
    columnOrder,
    projectUsersAll,
    filteredData,
    onOpenSidebar,
    getUserInfo,
    getStatusInfo,
    getPriorityInfo,
    formatLastUpdated,
    formatDeadline,
    level = 0,
    isExpanded = false,
    onToggleExpanded,
    onAddChildTask,
    expandedTasks,
    handleProjectPhaseChange,
    dropIntent,
    activeDragId,
    customOrder,
    allTasks = [],
    onRequestDurationChange,
    onRequestTimelineChange,
    onRequestModeChange,
    onRequestDeadlineChange,
    previewTaskId = null,
    scheduleTasks = [],
}) => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.projectData);
    const schedulingEnabled = isSchedulingMode(selectedProject);
    const statusOptions = getStatusOptionsForProject(selectedProject);
    const [editingDuration, setEditingDuration] = React.useState(false);
    const attachInputRef = React.useRef(null);
    const attachTaskIdRef = React.useRef(null);
    const allTasksForHierarchy = allTasks.length
        ? allTasks
        : filteredData?.isGrouped
          ? filteredData.groups?.flatMap(group => group.tasks) || []
          : filteredData?.data || filteredData || [];
    const childTasks = sortTasksByOrder(getChildren(task.id, allTasksForHierarchy), customOrder);
    const hasChildren = childTasks.length > 0;
    const displayTimeline = getInheritedTimeline(task, allTasksForHierarchy);
    const displayDeadline = getInheritedDeadline(task, allTasksForHierarchy);
    const displayDuration = getInheritedDuration(task, allTasksForHierarchy);
    const isDropTarget = dropIntent?.targetId === String(task.id);
    const showNestHint = isDropTarget && dropIntent?.valid && dropIntent?.label === 'nest';
    const showPromoteHint = isDropTarget && dropIntent?.valid && dropIntent?.label === 'promote';
    const showReorderHint = isDropTarget && dropIntent?.valid && dropIntent?.label === 'reorder';
    const showReorderAfterHint = isDropTarget && dropIntent?.valid && dropIntent?.label === 'reorder-after';
    const showInvalidHint = isDropTarget && dropIntent?.valid === false;
    const dropIntentMessage = isDropTarget ? getDropIntentMessage(dropIntent) : '';
    const isDragging = activeDragId === String(task.id);

    const isEditingName = editingTaskId === task.id;
    const dragDisabled = isEditingName || editingDuration;

    const { attributes, listeners, setNodeRef: setDragRef, isDragging: isDraggableActive } = useDraggable({
        id: String(task.id),
        disabled: dragDisabled,
        data: {
            type: 'task',
            task,
            level,
        },
    });

    const { setNodeRef: setDropRef } = useDroppable({
        id: String(task.id),
        data: {
            type: 'task',
            task,
            level,
        },
    });

    const setNodeRef = combineRefs(setDragRef, setDropRef);

    // Check if task can have children (allow up to 3 total levels: 0,1,2)
    // Level 0: Root tasks (can have children)
    // Level 1: First-level children (can have children)
    // Level 2: Second-level children (CANNOT have children)
    const canHaveChildren = level < 2;
    const hasReferences = (task.linked_records_count ?? 0) > 0;
    const hasAttachments = (task.attachments_count ?? 0) > 0;

    // Indentation based on level (row background stays white so status colours stay consistent)
    const getIndentation = () => {
        return level * 24; // 24px per level
    };

    const getBackgroundColor = () => (isTaskSelected(selectedRows, task.id) ? '#F2F6FE' : 'white');

    const formatTimelineDate = dateStr => {
        try {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime())) return '';
            return d.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
            });
        } catch {
            return '';
        }
    };

    const getDurationInDays = (startDateStr, endDateStr) => {
        if (!startDateStr || !endDateStr) return null;
        const start = new Date(startDateStr);
        const end = new Date(endDateStr);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
        const diffMs = end.getTime() - start.getTime();
        if (diffMs <= 0) return null;
        const dayMs = 1000 * 60 * 60 * 24;
        return Math.floor(diffMs / dayMs);
    };

    const formatDurationLabel = (startDateStr, endDateStr) => {
        const days = getDurationInDays(startDateStr, endDateStr);
        if (days == null) return '';

        if (days < 30) {
            return `${days} day${days === 1 ? '' : 's'}`;
        }

        if (days < 365) {
            const months = Math.floor(days / 30);
            const remainingDays = days - months * 30;
            if (remainingDays === 0) {
                return `${months} month${months === 1 ? '' : 's'}`;
            }
            return `${months} month${months === 1 ? '' : 's'} ${remainingDays} day${remainingDays === 1 ? '' : 's'}`;
        }

        const years = Math.floor(days / 365);
        const remainingDays = days - years * 365;
        const months = Math.floor(remainingDays / 30);
        if (months === 0) {
            return `${years} year${years === 1 ? '' : 's'}`;
        }
        return `${years} year${years === 1 ? '' : 's'} ${months} month${months === 1 ? '' : 's'}`;
    };

    // Handle adding child task
    const handleAddChildClick = e => {
        e.stopPropagation();
        // Always toggle expansion. For tasks without children, this opens/closes the add-child row.
        onToggleExpanded(task.id);
    };

    const handleAttachIconClick = e => {
        e.stopPropagation();
        if (hasAttachments) {
            onOpenSidebar?.(task, 'attachments-view');
            return;
        }
        attachTaskIdRef.current = task.id;
        onOpenSidebar?.(task, 'attachments');
        attachInputRef.current?.click();
    };

    const handleAttachFilesSelected = async e => {
        const taskId = attachTaskIdRef.current;
        const selected = Array.from(e.target.files || []).filter(file => {
            const ext = file.name.split('.').pop()?.toLowerCase();
            return isValidExtensionToUpload(ext);
        });
        e.target.value = '';
        if (!taskId || !selected.length) return;
        try {
            await dispatch(UploadTaskAttachment({ taskId, files: selected }));
        } catch (err) {
            console.error('Failed to upload attachment from task row:', err);
        }
    };

    const isPreviewLoading = previewTaskId != null && String(previewTaskId) === String(task.id);
    const visibleColumns = (columnOrder || DEFAULT_TASK_TABLE_COLUMN_ORDER)
        .filter(col => isColumnVisible(col))
        .filter(col => col !== 'mode' || schedulingEnabled);

    return (
        <>
            {/* Main task row */}
            <tr
                data-task-id={String(task.id)}
                ref={setNodeRef}
                {...(dragDisabled ? {} : attributes)}
                {...(dragDisabled ? {} : listeners)}
                role="row"
                className={`${styles.tableRow} ${normalizeTaskStatus(task.status) === 'CANCELLED' ? styles.cancelledRow : ''} ${isDragging || isDraggableActive ? styles.draggingRow : ''} ${isDropTarget ? `${styles.dragOverArea} ${styles.dropTargetRow}` : ''} ${showNestHint ? styles.nestTarget : ''} ${showPromoteHint ? styles.promoteTarget : ''} ${showReorderHint ? styles.reorderTarget : ''} ${showReorderAfterHint ? styles.reorderAfterTarget : ''} ${showInvalidHint ? styles.invalidTarget : ''} ${isPreviewLoading ? styles.previewLoadingRow : ''}`}
                style={{
                    opacity: isDragging || isDraggableActive ? 0.5 : isPreviewLoading ? 0.65 : 1,
                    backgroundColor: getBackgroundColor(),
                    pointerEvents: isPreviewLoading ? 'none' : undefined,
                }}
                onMouseEnter={e => {
                    const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                    if (checkbox && !isTaskSelected(selectedRows, task.id)) {
                        checkbox.style.opacity = '1';
                    }

                    // Show ArrowMaximize, Star, Attach, Link, Menu, Add, and Comment icons on hover
                    const arrowIcon = e.currentTarget.querySelector('.hover-icon');
                    const starIcon = e.currentTarget.querySelector('[data-icon-type="star"]');
                    const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                    const addIcon = e.currentTarget.querySelector('.hover-only-icon');
                    const commentIcon = e.currentTarget.querySelector('[data-icon-type="comment-add"]');
                    const attachIcon = e.currentTarget.querySelector('[data-icon-type="attach"]');
                    const linkIcon = e.currentTarget.querySelector('[data-icon-type="link"]');
                    if (arrowIcon) arrowIcon.style.display = 'flex';
                    if (starIcon) starIcon.style.opacity = '1';
                    if (menuIcon) menuIcon.style.display = 'flex';
                    if (addIcon) addIcon.style.opacity = '1';
                    if (commentIcon) commentIcon.style.opacity = '1';
                    if (attachIcon && !hasAttachments) attachIcon.style.opacity = '1';
                    if (linkIcon && !hasReferences) linkIcon.style.opacity = '1';
                }}
                onMouseLeave={e => {
                    const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                    if (checkbox && !isTaskSelected(selectedRows, task.id)) {
                        checkbox.style.opacity = '0';
                    }

                    // Hide ArrowMaximize icon on leave
                    const arrowIcon = e.currentTarget.querySelector('.hover-icon');
                    if (arrowIcon) arrowIcon.style.display = 'none';

                    // Hide Star icon only if task is not starred
                    const starIcon = e.currentTarget.querySelector('[data-icon-type="star"]');
                    if (starIcon && !task.starred) {
                        starIcon.style.opacity = '0';
                    }

                    // Hide Menu icon on leave
                    const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                    if (menuIcon) menuIcon.style.display = 'none';

                    // Hide Add icon on leave
                    const addIcon = e.currentTarget.querySelector('.hover-only-icon');
                    if (addIcon) addIcon.style.opacity = '0';

                    // Hide Add Comment icon on leave (filled comment stays visible)
                    const commentIcon = e.currentTarget.querySelector('[data-icon-type="comment-add"]');
                    if (commentIcon) commentIcon.style.opacity = '0';

                    const attachIcon = e.currentTarget.querySelector('[data-icon-type="attach"]');
                    if (attachIcon && !hasAttachments) attachIcon.style.opacity = '0';

                    const linkIcon = e.currentTarget.querySelector('[data-icon-type="link"]');
                    if (linkIcon && !hasReferences) linkIcon.style.opacity = '0';
                }}>
                {/* Checkbox column */}
                <td className={styles.tableRowItem} style={{ width: columnWidths.checkbox }}>
                    <input
                        className={styles.checkboxTable}
                        type="checkbox"
                        checked={isTaskSelected(selectedRows, task.id)}
                        onClick={e => e.stopPropagation()}
                        onChange={e => {
                            e.stopPropagation();
                            handleSelectRow(task.id, e.nativeEvent || e);
                        }}
                        style={{
                            opacity: isTaskSelected(selectedRows, task.id) ? 1 : 0,
                            transition: 'opacity 0.2s ease',
                        }}
                    />
                </td>

                {/* Dynamic columns based on order (includes task) */}
                {(visibleColumns)
                    .map(col => {
                        if (col === 'task') {
                            return (
                                <td
                                    key={col}
                                    className={`${styles.tableRowItem} ${styles.taskCellWithDropIndicator}`}
                                    style={{ width: columnWidths.task }}
                                    onMouseEnter={e => {
                                        const icon = e.currentTarget.querySelector('.hover-icon');
                                        if (icon) icon.style.display = 'flex';
                                    }}
                                    onMouseLeave={e => {
                                        const icon = e.currentTarget.querySelector('.hover-icon');
                                        if (icon) icon.style.display = 'none';
                                    }}>
                                    {isDropTarget && dropIntentMessage && (
                                        <span className={styles.dropIntentBadge}>{dropIntentMessage}</span>
                                    )}
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center" style={{ paddingLeft: getIndentation(), minWidth: 0, flex: '1 1 auto' }}>
                                            {canHaveChildren ? (
                                                <div className="me-2" style={{ cursor: 'pointer', minWidth: '16px', marginLeft: '8px' }} onClick={handleAddChildClick}>
                                                    {hasChildren ? (
                                                        isExpanded ? (
                                                            <SubtractCircle16Regular style={{ color: 'var(--brand-secondary, #8E2DE2)' }} />
                                                        ) : (
                                                            <AddCircle16Filled style={{ color: 'var(--brand-secondary, #8E2DE2)' }} />
                                                        )
                                                    ) : isExpanded ? (
                                                        <SubtractCircle16Regular style={{ color: 'var(--brand-secondary, #8E2DE2)' }} />
                                                    ) : (
                                                        <AddCircle16Filled className="hover-only-icon" style={{ color: '#C0C6D8', opacity: 0, transition: 'opacity 0.2s ease' }} />
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="me-2" style={{ minWidth: '16px', marginLeft: '8px' }} />
                                            )}
                                            <div className="flex-fill" style={{ minWidth: 0 }}>
                                                {editingTaskId === task.id ? (
                                                    <input
                                                        type="text"
                                                        className={'form-control form-control-sm ' + styles.taskTextField}
                                                        value={editingTaskText}
                                                        onPointerDown={e => e.stopPropagation()}
                                                        onMouseDown={e => e.stopPropagation()}
                                                        onChange={e => {
                                                            e.stopPropagation();
                                                            setEditingTaskText(e.target.value);
                                                        }}
                                                        onKeyDown={e => {
                                                            e.stopPropagation();
                                                            handleTaskTextKeyPress(e, task);
                                                        }}
                                                        onBlur={e => {
                                                            e.stopPropagation();
                                                            handleTaskTextSave(task, e.target.value);
                                                        }}
                                                        autoFocus
                                                        style={{ width: '100%' }}
                                                    />
                                                ) : (
                                                    <div
                                                        className={styles.taskText}
                                                        title={task.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : ''}
                                                        onClick={e => handleTaskTextClick(e, task)}
                                                        style={{
                                                            textDecoration: task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            width: '100%',
                                                        }}>
                                                        {task.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : ''}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex">
                                            <div
                                                className={styles.starIcon}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    if (hasReferences) {
                                                        onOpenSidebar?.(task, 'references-view');
                                                    } else {
                                                        onOpenSidebar?.(task, 'references');
                                                    }
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderLeft: 'none',
                                                    opacity: hasReferences ? 1 : 0,
                                                    transition: 'opacity 0.2s ease',
                                                }}
                                                data-icon-type="link"
                                                title={hasReferences ? 'View references' : 'Add reference'}>
                                                <Link16Regular
                                                    style={hasReferences ? { color: 'var(--Brand-Secondary)' } : undefined}
                                                />
                                            </div>
                                            <div
                                                className={styles.starIcon}
                                                onClick={handleAttachIconClick}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderLeft: 'none',
                                                    opacity: hasAttachments ? 1 : 0,
                                                    transition: 'opacity 0.2s ease',
                                                }}
                                                data-icon-type="attach"
                                                title={hasAttachments ? 'View attachments' : 'Add attachment'}>
                                                <Attach16Regular
                                                    style={hasAttachments ? { color: 'var(--Brand-Secondary)' } : undefined}
                                                />
                                                <input
                                                    ref={attachInputRef}
                                                    type="file"
                                                    multiple
                                                    accept={validExtensions.map(ext => `.${ext}`).join(',')}
                                                    style={{ display: 'none' }}
                                                    onChange={handleAttachFilesSelected}
                                                />
                                            </div>
                                            <div
                                                className={styles.starIcon}
                                                style={{ cursor: 'pointer', borderLeft: 'none' }}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onOpenSidebar(task);
                                                }}>
                                                <div className="hover-icon" style={{ display: 'none', width: '12px', height: '12px' }}>
                                                    <ArrowMaximize16Regular style={{ width: '12px', height: '12px', color: 'var(--Brand-Secondary)' }} />
                                                </div>
                                            </div>
                                            <div
                                                className={styles.starIcon}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleStarClick(e, task);
                                                }}
                                                style={{ cursor: 'pointer', borderLeft: 'none', opacity: task.starred ? 1 : 0, transition: 'opacity 0.2s ease' }}
                                                data-icon-type="star">
                                                {task.starred ? <Star16Filled style={{ color: '#FFD700' }} /> : <Star16Regular />}
                                            </div>
                                            <div
                                                className={styles.commentIcon}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onOpenSidebar(task);
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    opacity: task?.comments_count > 0 ? 1 : 0,
                                                    transition: 'opacity 0.2s ease',
                                                }}
                                                data-icon-type={task?.comments_count > 0 ? 'comment' : 'comment-add'}>
                                                {task?.comments_count > 0 ? <Comment16Filled color="var(--Brand-Secondary)" /> : <CommentAdd16Regular />}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            );
                        }
                        if (col === 'owner') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.owner }}>
                                    <UserAssignmentMenu task={task} onUserSelect={user => handleUserAssignment(task, user)}>
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            {(() => {
                                                const userInfo = getUserInfo(task.assigned_to);
                                                if (userInfo) {
                                                    return <CustomAvatar name={userInfo.full_name} size={20} />;
                                                }
                                                return (
                                                    <div
                                                        className={styles.unassignedOwner}
                                                        style={{ width: 20, height: 20 }}
                                                        title="Assign owner">
                                                        <Add16Regular style={{ width: 12, height: 12, color: '#989BAD' }} />
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </UserAssignmentMenu>
                                </td>
                            );
                        }
                        if (col === 'status') {
                            const statusLocked = schedulingEnabled && hasChildren;
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.status }}>
                                    <Menu>
                                        <MenuTrigger disableButtonEnhancement={statusLocked}>
                                            <div
                                                className={styles.statusDiv}
                                                style={{
                                                    backgroundColor: getStatusInfo(task?.status)?.bg,
                                                    color: getStatusInfo(task?.status)?.color || 'white',
                                                    border:
                                                        getStatusInfo(task?.status)?.border ||
                                                        (getStatusInfo(task?.status)?.bg === '#FFFFFF' ? '1px solid #DDDEE4' : undefined),
                                                    cursor: statusLocked ? 'default' : 'pointer',
                                                }}
                                                title={statusLocked ? 'Status is calculated from subtasks' : undefined}>
                                                {getStatusInfo(task?.status)?.text || task?.status}
                                            </div>
                                        </MenuTrigger>
                                        {!statusLocked && (
                                            <MenuPopover style={{ width: '118px', minWidth: '118px' }}>
                                                <MenuList>
                                                    {statusOptions.map(opt => (
                                                        <MenuItem
                                                            key={opt.value}
                                                            className={styles.statusMenuItem}
                                                            onClick={() => handleStatusChange(task, opt.value)}
                                                            style={{
                                                                backgroundColor: opt.bg,
                                                                color: opt.color,
                                                                border: opt.border,
                                                            }}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </MenuList>
                                            </MenuPopover>
                                        )}
                                    </Menu>
                                </td>
                            );
                        }
                        if (col === 'timeline') {
                            const start = formatTimelineDate(displayTimeline.start_date);
                            const end = formatTimelineDate(displayTimeline.end_date);
                            const timelinePlaceholder = 'dd/MM/yyyy';
                            const timelineLabel =
                                start && end ? `${start} - ${end}` : start ? `${start} - ${timelinePlaceholder}` : end ? `${timelinePlaceholder} - ${end}` : '';
                            const timelineContent = (
                                <div
                                    className="d-flex align-items-center justify-content-center px-2"
                                    style={{ height: '32px', width: '100%', cursor: displayTimeline.isInherited ? 'default' : 'pointer' }}
                                    title={displayTimeline.isInherited ? 'Timeline is calculated from subtasks' : undefined}>
                                    <span className={styles.tableRegularText} style={{ whiteSpace: 'nowrap' }}>
                                        {timelineLabel}
                                    </span>
                                </div>
                            );
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.timeline }}>
                                    {displayTimeline.isInherited ? (
                                        timelineContent
                                    ) : (
                                        <TimelineRangePickerMenu
                                            task={task}
                                            onRequestTimelineChange={onRequestTimelineChange}
                                            schedulingConfirmRequired={schedulingEnabled}>
                                            {timelineContent}
                                        </TimelineRangePickerMenu>
                                    )}
                                </td>
                            );
                        }
                        if (col === 'duration') {
                            const durationReadOnly = !schedulingEnabled || displayTimeline.isInherited || hasChildren;
                            const rolledUpDuration = task.duration ?? displayDuration.duration;
                            const durationLabel = schedulingEnabled
                                ? hasChildren
                                    ? formatDuration(rolledUpDuration)
                                    : formatDuration(task.duration) ||
                                      formatDurationLabel(displayTimeline.start_date, displayTimeline.end_date)
                                : formatDurationLabel(displayTimeline.start_date, displayTimeline.end_date);

                            return (
                                <td
                                    key={col}
                                    className={styles.tableRowItem}
                                    style={{ width: columnWidths.duration }}
                                    onPointerDown={e => e.stopPropagation()}
                                    onMouseDown={e => e.stopPropagation()}>
                                    <DurationPickerMenu
                                        durationMinutes={hasChildren ? rolledUpDuration : task.duration}
                                        durationLabel={durationLabel}
                                        readOnly={durationReadOnly}
                                        readOnlyTitle={durationReadOnly && schedulingEnabled ? 'Duration is calculated from subtasks' : undefined}
                                        placeholder="—"
                                        onEditingChange={setEditingDuration}
                                        onSave={async parsed => {
                                            if (onRequestDurationChange) {
                                                onRequestDurationChange(task, parsed);
                                                return;
                                            }
                                            await dispatch(PatchTask({ id: task.id, body: { duration: parsed } }));
                                        }}
                                    />
                                </td>
                            );
                        }
                        if (col === 'mode') {
                            const modeReadOnly = hasChildren;
                            const modeLabel = modeReadOnly ? '—' : task.mode || 'FS';
                            const modeCell = modeReadOnly ? (
                                <ModePickerMenu
                                    task={task}
                                    allTasks={allTasksForHierarchy}
                                    scheduleTasks={scheduleTasks}
                                    customOrder={customOrder}
                                    readOnly
                                    readOnlyReason="parent"
                                />
                            ) : (
                                <ModePickerMenu
                                    task={task}
                                    allTasks={allTasksForHierarchy}
                                    scheduleTasks={scheduleTasks}
                                    customOrder={customOrder}
                                    onModeSelect={(t, mode) => onRequestModeChange && onRequestModeChange(t, mode)}>
                                    <div
                                        className="d-flex align-items-center justify-content-center px-2"
                                        style={{ height: '32px', width: '100%', cursor: 'pointer' }}>
                                        <span className={styles.tableRegularText}>
                                            {modeLabel} ▾
                                        </span>
                                    </div>
                                </ModePickerMenu>
                            );
                            return (
                                <td
                                    key={col}
                                    className={styles.tableRowItem}
                                    style={{ width: columnWidths.mode }}
                                    onPointerDown={e => e.stopPropagation()}
                                    onMouseDown={e => e.stopPropagation()}>
                                    {modeCell}
                                </td>
                            );
                        }
                        if (col === 'deadline') {
                            // Phase 1: parent Deadline remains editable (F-12); Timeline/Duration stay derived.
                            const deadlineValue = hasChildren ? task.deadline || displayDeadline.deadline : task.deadline;
                            const deadlineReadOnly = false;
                            const deadlineContent = (
                                <div className="d-flex align-items-center gap-1 px-2" style={{ cursor: deadlineReadOnly ? 'default' : 'pointer', width: '100%' }}>
                                    {(() => {
                                        const isCompleted = task.status === 'COMPLETED' || task.status === 'COMPLETE';
                                        const deadline = deadlineValue ? new Date(deadlineValue) : null;
                                        const today = new Date();
                                        if (deadline) {
                                            deadline.setHours(0, 0, 0, 0);
                                        }
                                        today.setHours(0, 0, 0, 0);

                                        const formattedDeadline = deadline
                                            ? deadline.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                                            : '\u00A0';

                                        return (
                                            <>
                                                <div className="d-flex align-items-center justify-content-center" style={{ flexShrink: 0 }}>
                                                    {(() => {
                                                        if (!deadline || task.status === 'CANCELLED') {
                                                            return null;
                                                        }

                                                        // D-01: Off-track = Planned Finish > Deadline
                                                        const offTrack = taskIsOffTrack({
                                                            ...task,
                                                            deadline: deadlineValue,
                                                            end_date: task.end_date || displayTimeline.end_date,
                                                        });
                                                        const isOverdue = today > deadline;

                                                        if (offTrack && !isCompleted) {
                                                            return (
                                                                <span className={styles.offTrackPill} title="Off-track: planned finish is after deadline">
                                                                    <span aria-hidden="true">!</span>
                                                                    Off-track
                                                                </span>
                                                            );
                                                        }

                                                        if (isOverdue && isCompleted) {
                                                            return <Checkmark16Regular style={{ color: 'var(--RAG-Green)' }} />;
                                                        }

                                                        if (isOverdue && !isCompleted) {
                                                            return <ErrorCircle16Regular style={{ color: 'var(--RAG-Red)' }} />;
                                                        }

                                                        return null;
                                                    })()}
                                                </div>
                                                <span
                                                    className={styles.tableRegularText}
                                                    style={{
                                                        textDecoration: isCompleted || task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                        color: taskIsOffTrack({
                                                            ...task,
                                                            deadline: deadlineValue,
                                                            end_date: task.end_date || displayTimeline.end_date,
                                                        })
                                                            ? 'var(--RAG-Red)'
                                                            : undefined,
                                                    }}
                                                    title={
                                                        taskIsOffTrack({
                                                            ...task,
                                                            deadline: deadlineValue,
                                                            end_date: task.end_date || displayTimeline.end_date,
                                                        })
                                                            ? 'Off-track: planned finish is after deadline'
                                                            : undefined
                                                    }>
                                                    {formattedDeadline}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                            );

                            return (
                                <td
                                    key={col}
                                    className={styles.tableRowItem}
                                    style={{ width: columnWidths.deadline }}
                                    title={deadlineReadOnly ? 'Deadline is calculated from subtasks' : undefined}>
                                    {deadlineReadOnly ? deadlineContent : (
                                        <DeadlinePickerMenu
                                            task={task}
                                            onRequestDeadlineChange={
                                                onRequestDeadlineChange
                                                    ? deadlineData => onRequestDeadlineChange(task, deadlineData)
                                                    : undefined
                                            }
                                            onDeadlineSelect={deadlineData => handleDeadlineSelect(task, deadlineData)}>
                                            {deadlineContent}
                                        </DeadlinePickerMenu>
                                    )}
                                </td>
                            );
                        }
                        if (col === 'priority') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.priority }}>
                                    <Menu>
                                        <MenuTrigger>
                                            {(() => {
                                                const priorityInfo = getPriorityInfo(task.priority);
                                                return (
                                                    <div
                                                        className={styles.statusDiv}
                                                        style={{
                                                            background: priorityInfo.bg,
                                                            color: priorityInfo.color || 'white',
                                                            border: priorityInfo.bg === '#FFFFFF' ? '1px solid #DDDEE4' : undefined,
                                                            cursor: 'pointer',
                                                        }}>
                                                        {priorityInfo.text || ''}
                                                    </div>
                                                );
                                            })()}
                                        </MenuTrigger>
                                        <MenuPopover style={{ width: '117px', minWidth: '117px' }}>
                                            <MenuList>
                                                <MenuItem
                                                    className={styles.priorityMenuItem}
                                                    onClick={() => handlePriorityChange(task, 'HIGH')}
                                                    style={{ backgroundColor: '#2B35AF', color: 'white' }}>
                                                    High
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.priorityMenuItem}
                                                    onClick={() => handlePriorityChange(task, 'MEDIUM')}
                                                    style={{ backgroundColor: '#4361EE', color: 'white' }}>
                                                    Medium
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.priorityMenuItem}
                                                    onClick={() => handlePriorityChange(task, 'LOW')}
                                                    style={{ backgroundColor: '#4895EF', color: 'white' }}>
                                                    Low
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.priorityMenuItem}
                                                    onClick={() => handlePriorityChange(task, '')}
                                                    style={{ backgroundColor: '#FFFFFF', color: '#989BAD', border: '1px solid #DDDEE4' }}>
                                                    None
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                </td>
                            );
                        }
                        if (col === 'workstream') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.workstream }}>
                                    <WorkstreamManagementMenu
                                        onWorkstreamSelect={workstream => handleWorkstreamSelect(task, workstream)}
                                        taskData={filteredData}
                                        selectedWorkstreamName={task.workstream_repr}>
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <span
                                                className={styles.tableRegularText}
                                                style={{
                                                    textDecoration: task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                }}>
                                                {task.workstream_repr || ''}
                                            </span>
                                        </div>
                                    </WorkstreamManagementMenu>
                                </td>
                            );
                        }
                        if (col === 'tag') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.tag }}>
                                    <TagManagementMenu
                                        onTagSelect={tag => handleTagSelect(task, tag)}
                                        taskData={filteredData}
                                        taskWorkstreamName={task.workstream_repr}
                                        selectedTagName={getVisibleTaskTags(task).join(', ')}>
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            {(() => {
                                                const tagsArray = getVisibleTaskTags(task);

                                                let displayLabel = '';
                                                if (tagsArray.length > 0) {
                                                    const [first, ...rest] = tagsArray;
                                                    displayLabel = rest.length === 0 ? first : `${first}, +${rest.length}`;
                                                }

                                                return (
                                                    <Tooltip
                                                        relationship="description"
                                                        content={
                                                            tagsArray.length > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                    {tagsArray.map(tag => (
                                                                        <span key={tag}>{tag}</span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                ''
                                                            )
                                                        }>
                                                        <span
                                                            className={styles.tableRegularText}
                                                            style={{
                                                                textDecoration: task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                            }}>
                                                            {displayLabel}
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </div>
                                    </TagManagementMenu>
                                </td>
                            );
                        }
                        if (col === 'projectPhase') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.projectPhase }}>
                                    <ProjectPhaseManagementMenu
                                        onPhaseSelect={phase => handleProjectPhaseChange(task, phase)}
                                        taskData={filteredData}
                                        selectedPhaseName={task.project_phase}>
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <span
                                                className={styles.tableRegularText}
                                                style={{
                                                    textDecoration: task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                }}>
                                                {task.project_phase || ''}
                                            </span>
                                        </div>
                                    </ProjectPhaseManagementMenu>
                                </td>
                            );
                        }
                        if (col === 'lastUpdated') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.lastUpdated }}>
                                    <div className="d-flex align-items-center gap-2 px-2 justify-content-center">
                                        <div>
                                            <CustomAvatar name={task.last_updated_by_name} size={20} />
                                        </div>
                                        <span style={{ width: 60 }} className={styles.tableRegularText}>
                                            {formatLastUpdated(task.last_updated)}
                                        </span>
                                    </div>
                                </td>
                            );
                        }
                        if (col === 'addedOn') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.addedOn }}>
                                    <div className="d-flex align-items-center gap-2 px-2 justify-content-center">
                                        <div>
                                            <CustomAvatar name={task.created_by_name} size={20} />
                                        </div>
                                        <span style={{ width: 60 }} className={styles.tableRegularText}>
                                            {task.created ? new Date(task.created).toLocaleDateString() : ''}
                                        </span>
                                    </div>
                                </td>
                            );
                        }
                        return null;
                    })}

                {/* Actions column */}
                <td className={styles.tableRowItem} style={{ width: columnWidths.actions }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <div className="menu-hover-icon" style={{ display: 'none' }}>
                            <Menu>
                                <MenuTrigger>
                                    <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <MoreVertical16Regular style={{ width: '16px', height: '16px' }} />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem onClick={() => handleDelete(task)}>Delete</MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        </div>
                    </div>
                </td>
            </tr>

            {/* Render child tasks if expanded */}
            {isExpanded &&
                childTasks.map((childTask, childIndex) => (
                    <HierarchicalTaskRow
                        key={childTask.id}
                        task={childTask}
                        index={childIndex}
                        editingTaskId={editingTaskId}
                        setEditingTaskId={setEditingTaskId}
                        editingTaskText={editingTaskText}
                        setEditingTaskText={setEditingTaskText}
                        handleTaskTextClick={handleTaskTextClick}
                        handleTaskTextSave={handleTaskTextSave}
                        handleTaskTextCancel={handleTaskTextCancel}
                        handleTaskTextKeyPress={handleTaskTextKeyPress}
                        handleStarClick={handleStarClick}
                        handleUserAssignment={handleUserAssignment}
                        handleDeadlineSelect={handleDeadlineSelect}
                        handleStatusChange={handleStatusChange}
                        handlePriorityChange={handlePriorityChange}
                        handleTagSelect={handleTagSelect}
                        handleWorkstreamSelect={handleWorkstreamSelect}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleSelectRow={handleSelectRow}
                        selectedRows={selectedRows}
                        isColumnVisible={isColumnVisible}
                        columnWidths={columnWidths}
                        columnOrder={columnOrder}
                        projectUsersAll={projectUsersAll}
                        filteredData={filteredData}
                        onOpenSidebar={onOpenSidebar}
                        getUserInfo={getUserInfo}
                        getStatusInfo={getStatusInfo}
                        getPriorityInfo={getPriorityInfo}
                        formatLastUpdated={formatLastUpdated}
                        formatDeadline={formatDeadline}
                        level={level + 1}
                        isExpanded={expandedTasks?.has ? expandedTasks.has(childTask.id) : false} // Use expandedTasks state
                        onToggleExpanded={onToggleExpanded}
                        onAddChildTask={onAddChildTask}
                        onRequestDurationChange={onRequestDurationChange}
                        onRequestTimelineChange={onRequestTimelineChange}
                        onRequestModeChange={onRequestModeChange}
                        onRequestDeadlineChange={onRequestDeadlineChange}
                        previewTaskId={previewTaskId}
                        expandedTasks={expandedTasks}
                        handleProjectPhaseChange={handleProjectPhaseChange}
                        dropIntent={dropIntent}
                        activeDragId={activeDragId}
                        customOrder={customOrder}
                        allTasks={allTasksForHierarchy}
                        scheduleTasks={scheduleTasks}
                    />
                ))}

            {/* Empty row for adding new child tasks with existing background color */}
            {isExpanded && canHaveChildren && !childTasks.some(child => (expandedTasks?.has ? expandedTasks.has(child.id) : false)) && (
                <tr className={styles.tableRow} style={{ backgroundColor: '#F3FDFE' }}>
                    <td className={styles.tableRowItem} style={{ width: columnWidths.checkbox }}>
                        <input className={styles.checkboxTable} type="checkbox" disabled style={{ opacity: 0.5 }} />
                    </td>
                    {(visibleColumns)
                        .map(col => {
                            if (col === 'task') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.task }}>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center" style={{ paddingLeft: getIndentation() + 24 }}>
                                                <div className="me-2" style={{ minWidth: '16px', marginLeft: '8px' }}></div>
                                                <div className="flex-fill">
                                                    <input
                                                        type="text"
                                                        className={'form-control form-control-sm ' + styles.taskTextField}
                                                        placeholder={level === 0 ? 'Add new subtask' : 'Add new action item'}
                                                        autoFocus
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') {
                                                                if (e.target.value.trim()) {
                                                                    onAddChildTask(task.id, e.target.value.trim());
                                                                    e.target.value = '';
                                                                }
                                                            } else if (e.key === 'Escape') {
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                        onBlur={e => {
                                                            if (e.target.value.trim()) {
                                                                onAddChildTask(task.id, e.target.value.trim());
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="d-flex">
                                                <div className={styles.starIcon} style={{ opacity: 0.5, borderLeft: 'none' }}>
                                                    <Star16Regular />
                                                </div>
                                                <div className={styles.commentIcon} style={{ opacity: 0 }}>
                                                    <CommentAdd16Regular />
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'owner') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.owner }}>
                                        <div style={{ width: '100%', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <div className={styles.unassignedOwner} style={{ width: 20, height: 20, opacity: 0.5 }}>
                                                <Add16Regular style={{ width: 12, height: 12, color: '#989BAD' }} />
                                            </div>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'status') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.status }}>
                                        <div className={styles.statusDiv} style={{ backgroundColor: '#FFFFFF', color: '#989BAD', opacity: 0.5 }}>
                                            Not Started
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'timeline') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.timeline }}>
                                        <div className="d-flex align-items-center justify-content-center px-2" style={{ height: '32px', opacity: 0.5, width: '100%' }}>
                                            <span className={styles.tableRegularText} style={{ whiteSpace: 'nowrap' }}></span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'deadline') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.deadline }}>
                                        <div className="d-flex align-items-center gap-1 px-2">
                                            <div className="d-flex align-items-center justify-content-center" style={{ width: '16px' }}></div>
                                            <span className={styles.tableRegularText} style={{ opacity: 0.5 }}></span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'duration') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.duration }}>
                                        <div className="d-flex align-items-center gap-1 px-2">
                                            <span className={styles.tableRegularText} style={{ opacity: 0.5 }}></span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'mode') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.mode }}>
                                        <div className="d-flex align-items-center justify-content-center px-2" style={{ height: '32px', opacity: 0.5, width: '100%' }}>
                                            <span className={styles.tableRegularText}>FS</span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'priority') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.priority }}>
                                        <div className={styles.statusDiv} style={{ background: '#FFFFFF', color: '#989BAD', opacity: 0.5 }}>
                                            None
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'workstream') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.workstream }}>
                                        <div className=" gap-1 px-2">
                                            <span className={styles.tableRegularText + ' text-center'} style={{ opacity: 0.5 }}></span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'tag') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.tag }}>
                                        <div className=" gap-1 px-2">
                                            <span className={styles.tableRegularText} style={{ opacity: 0.5 }}></span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'projectPhase') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.projectPhase }}>
                                        <div className=" gap-1 px-2">
                                            <span className={styles.tableRegularText + ' text-center'} style={{ opacity: 0.5 }}></span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'lastUpdated') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.lastUpdated }}>
                                        <div className="d-flex align-items-center gap-2 px-2">
                                            <span className={styles.tableRegularText + ' m-auto'} style={{ opacity: 0.5 }}></span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'addedOn') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.addedOn }}>
                                        <div className="d-flex align-items-center gap-2 px-2">
                                            <span className={styles.tableRegularText + ' m-auto'} style={{ opacity: 0.5 }}></span>
                                        </div>
                                    </td>
                                );
                            }
                            return null;
                        })}
                    <td className={styles.tableRowItem} style={{ width: columnWidths.actions }}>
                        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MoreVertical16Regular style={{ opacity: 0.5 }} />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const dropIntentEqual = (prev, next) =>
    (prev?.targetId ?? null) === (next?.targetId ?? null) &&
    prev?.label === next?.label &&
    prev?.valid === next?.valid &&
    prev?.reason === next?.reason;

const areRowPropsEqual = (prev, next) => {
    if (prev.task !== next.task) return false;
    if (prev.level !== next.level) return false;
    if (prev.isExpanded !== next.isExpanded) return false;
    if (prev.editingTaskId !== next.editingTaskId) return false;
    const editingThisRow = next.editingTaskId === next.task.id || prev.editingTaskId === prev.task.id;
    if (editingThisRow && prev.editingTaskText !== next.editingTaskText) return false;
    if (prev.customOrder !== next.customOrder) return false;
    if (prev.allTasks !== next.allTasks) return false;
    if (prev.columnWidths !== next.columnWidths) return false;
    if (prev.columnOrder !== next.columnOrder) return false;

    const rowId = String(prev.task.id);
    if ((prev.activeDragId === rowId) !== (next.activeDragId === rowId)) {
        return false;
    }

    const wasTarget = prev.dropIntent?.targetId === rowId;
    const isTarget = next.dropIntent?.targetId === rowId;
    if ((wasTarget || isTarget) && !dropIntentEqual(prev.dropIntent, next.dropIntent)) {
        return false;
    }

    const prevSelected = isTaskSelected(prev.selectedRows, prev.task.id);
    const nextSelected = isTaskSelected(next.selectedRows, next.task.id);
    if (prevSelected !== nextSelected) return false;

    return true;
};

export default React.memo(HierarchicalTaskRow, areRowPropsEqual);
